// components/AuthModal.js (Enhanced with animations)
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const AuthModal = ({ open, onClose, mode }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleRoleSelect = (role) => {
    onClose();

    if (role === "student") {
      if (mode === "login") {
        navigate("/userlogin");
      } else {
        navigate("/usersignup");
      }
    } else if (role === "instructor") {
      if (mode === "login") {
        navigate("/teacherlogin");
      } else {
        navigate("/teachersignup");
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
          borderRadius: fullScreen ? 0 : 3,
          color: "#1a1a1a",
          border: "1px solid rgba(43, 173, 101, 0.1)",
          position: "relative",
          overflow: "visible",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "rgba(26, 26, 26, 0.4)",
          "&:hover": {
            color: "#2BAD65",
            backgroundColor: "rgba(43, 173, 101, 0.05)",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ pt: 4, pb: 1, textAlign: "center" }}>
        <Typography
          variant="h4"
          component="span"
          color="#1a1a1a"
          fontWeight={700}
        >
          {mode === "login" ? "Welcome Back!" : "Join Wavepalm Techub"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: "rgba(26, 26, 26, 0.6)",
            fontWeight: 400,
          }}
        >
          {mode === "login"
            ? "Choose your role to continue learning"
            : "Start your learning journey today"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Student Option */}
          <Paper
            elevation={0}
            onClick={() => handleRoleSelect("student")}
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            sx={{
              p: 3,
              cursor: "pointer",
              backgroundColor: "rgba(43, 173, 101, 0.04)",
              border: "2px solid rgba(43, 173, 101, 0.08)",
              borderRadius: 2,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(43, 173, 101, 0.08)",
                borderColor: "#2BAD65",
                boxShadow: "0 8px 25px rgba(43, 173, 101, 0.12)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(43, 173, 101, 0.1)",
                  borderRadius: "50%",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SchoolIcon sx={{ color: "#2BAD65", fontSize: 32 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" color="#1a1a1a" fontWeight={600}>
                  Student
                </Typography>
                <Typography variant="body2" color="rgba(26, 26, 26, 0.5)">
                  Access courses, track progress, and learn at your own pace
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: "#2BAD65",
                  color: "#ffffff",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.2rem",
                  fontWeight: 600,
                }}
              >
                →
              </Box>
            </Box>
          </Paper>

          {/* Instructor Option */}
          <Paper
            elevation={0}
            onClick={() => handleRoleSelect("instructor")}
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            sx={{
              p: 3,
              cursor: "pointer",
              backgroundColor: "rgba(160, 239, 72, 0.04)",
              border: "2px solid rgba(160, 239, 72, 0.08)",
              borderRadius: 2,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(160, 239, 72, 0.08)",
                borderColor: "#A0EF48",
                boxShadow: "0 8px 25px rgba(160, 239, 72, 0.12)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(160, 239, 72, 0.1)",
                  borderRadius: "50%",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PersonIcon sx={{ color: "#A0EF48", fontSize: 32 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" color="#1a1a1a" fontWeight={600}>
                  Instructor
                </Typography>
                <Typography variant="body2" color="rgba(26, 26, 26, 0.5)">
                  Create courses, share knowledge, and earn from teaching
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: "#A0EF48",
                  color: "#1a1a1a",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.2rem",
                  fontWeight: 600,
                }}
              >
                →
              </Box>
            </Box>
          </Paper>

          <Divider
            sx={{ my: 2, backgroundColor: "rgba(43, 173, 101, 0.08)" }}
          />

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="rgba(26, 26, 26, 0.4)">
              {mode === "login"
                ? "Don't have an account?"
                : "Already have an account?"}
              <Button
                variant="text"
                onClick={() => {
                  if (mode === "login") {
                    navigate("/usersignup");
                  } else {
                    navigate("/userlogin");
                  }
                  onClose();
                }}
                sx={{
                  color: "#2BAD65",
                  fontWeight: 600,
                  ml: 0.5,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(43, 173, 101, 0.05)",
                  },
                }}
              >
                {mode === "login" ? "Sign Up" : "Login"}
              </Button>
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
        <Typography variant="caption" color="rgba(26, 26, 26, 0.3)">
          By continuing, you agree to our Terms of Service & Privacy Policy
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default AuthModal;
